import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { TriangleAlert, LoaderCircle } from 'lucide-react';

// Single landing spot for every emailed auth link.
//
// The important property is that this is OUR url, not GoTrue's
// /auth/v1/verify. Mail scanners — Microsoft Defender Safe Links above all —
// fetch every link in an inbound message before the recipient sees it. A GET
// against GoTrue's verify endpoint *consumes* the single-use token, so by the
// time a human clicks, the link is spent. A GET against this page returns
// static HTML; redeeming the token needs JavaScript, which no scanner runs.
//
// Shape: /auth/confirm?token_hash=...&type=recovery&next=/reset-password
export default function AuthConfirmPage() {
  const { verifyRecoveryToken } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const ranRef = useRef(false);

  useEffect(() => {
    // Single-use token: StrictMode's double-invoke would spend it twice.
    if (ranRef.current) return;
    ranRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const tokenHash = params.get('token_hash');
    const type = params.get('type') || 'recovery';
    const rawNext = params.get('next') || '/app';
    // Only ever redirect within this app — never honour an absolute URL from
    // a query string, or this becomes an open redirect.
    const next = rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : '/app';

    const urlError = params.get('error_description') || params.get('error');
    if (urlError) {
      setError('This link has expired or was already used.');
      return;
    }
    if (!tokenHash) {
      setError('This link is missing its verification token.');
      return;
    }

    (async () => {
      const { error: err } = await verifyRecoveryToken(tokenHash, type);
      if (err) {
        setError('This link has expired or was already used. Links are valid for one hour and can only be opened once.');
        return;
      }
      navigate(next, { replace: true });
    })();
  }, [verifyRecoveryToken, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary p-4">
      <div className="w-full max-w-[420px] text-center">
        {error ? (
          <>
            <div className="w-[48px] h-[48px] bg-danger-soft text-danger rounded-[14px] flex items-center justify-center mx-auto mb-4">
              <TriangleAlert size={24} strokeWidth={2.5} />
            </div>
            <h1 className="text-[22px] font-bold text-primary-c mb-2">This link can't be used</h1>
            <p className="text-[13px] text-muted-c mb-6">{error}</p>
            <Link
              to="/forgot-password"
              className="text-[13px] font-semibold text-accent-light hover:text-accent transition-colors"
            >
              Request a new link
            </Link>
          </>
        ) : (
          <>
            <div className="w-[48px] h-[48px] bg-accent-glow text-accent-light rounded-[14px] flex items-center justify-center mx-auto mb-4">
              <LoaderCircle size={24} strokeWidth={2.5} className="animate-spin" />
            </div>
            <h1 className="text-[22px] font-bold text-primary-c mb-2">Verifying your link</h1>
            <p className="text-[13px] text-muted-c">One moment...</p>
          </>
        )}
      </div>
    </div>
  );
}
